import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Scissors,
  CalendarClock,
  CalendarDays,
  Users,
  Star,
  Landmark,
  Menu,
  X,
  LogOut,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCentreAuth } from '@/context/CentreAuthContext';
import { DownloadAppButton } from '@/components/common/DownloadAppButton';
import { PayoutSetupBanner } from '@/components/centre/PayoutSetupBanner';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

/* Grouped the way a practitioner thinks about their portal: the snapshot, the
   clinic they maintain, and the activity flowing through it. */
const navGroups: { title: string; items: NavItem[] }[] = [
  {
    title: 'Overview',
    items: [{ to: '/partner/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true }],
  },
  {
    title: 'My Clinic',
    items: [
      { to: '/partner/listing', label: 'Clinic Profile', icon: Building2 },
      { to: '/partner/services', label: 'Services & Pricing', icon: Scissors },
      { to: '/partner/availability', label: 'Availability', icon: CalendarClock },
      { to: '/partner/payouts', label: 'Payment Settlement', icon: Landmark },
    ],
  },
  {
    title: 'Activity',
    items: [
      { to: '/partner/bookings', label: 'Bookings', icon: CalendarDays },
      { to: '/partner/patients', label: 'Patients', icon: Users },
      { to: '/partner/reviews', label: 'Reviews', icon: Star },
    ],
  },
];

const INK = '#0D2137'; // brand navy — the console rail (shared with admin)
const MINT = '#2DD4BF'; // brand teal, brightened to read on navy

function Sidebar({ onClose }: { onClose?: () => void }) {
  const { signOutCentre, centre } = useCentreAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    signOutCentre();
    navigate('/partner/login');
  };

  const clinicName = centre?.name?.trim() || 'Your clinic';

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: INK }}>
      {/* Brand */}
      <div className="flex h-16 shrink-0 items-center justify-between px-5">
        <div className="flex items-center gap-2.5">
          <div className="leading-none">
            <span className="block text-[15px] font-extrabold tracking-tight text-white">recovry</span>
            <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">
              Clinic Portal
            </span>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close menu"
          >
            <X className="size-5" />
          </button>
        )}
      </div>

      <ScrollArea className="min-h-0 flex-1 px-3">
        <nav className="space-y-5 pb-4">
          {navGroups.map((group) => (
            <div key={group.title}>
              <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35">
                {group.title}
              </p>
              <div className="space-y-0.5">
                {group.items.map(({ to, label, icon: Icon, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors',
                        isActive
                          ? 'bg-white/10 text-white'
                          : 'text-white/60 hover:bg-white/[0.06] hover:text-white',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full transition-opacity"
                          style={{ backgroundColor: MINT, opacity: isActive ? 1 : 0 }}
                        />
                        <Icon
                          className="size-[17px] shrink-0 transition-colors"
                          style={{ color: isActive ? MINT : undefined }}
                        />
                        {label}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Clinic identity + sign out */}
      <div className="shrink-0 border-t border-white/10 p-3">
        <div className="mb-1.5 flex items-center gap-2.5 px-2">
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
            style={{ backgroundColor: 'rgba(45,212,191,0.18)', color: MINT }}
          >
            {clinicName.charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0 leading-tight">
            <p className="truncate text-xs font-semibold text-white">{clinicName}</p>
            <p className="truncate text-[11px] text-white/40">Partner clinic</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium text-white/60 transition-colors hover:bg-red-500/15 hover:text-red-300"
        >
          <LogOut className="size-4" /> Log out
        </button>
      </div>
    </div>
  );
}

export function CentreOwnerLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden bg-[#F5F7FA] dark:bg-background">
      {/* Desktop rail */}
      <aside className="hidden w-64 shrink-0 lg:block">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]" onClick={() => setMobileOpen(false)} />
          <aside className="relative h-full w-64">
            <Sidebar onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* On desktop this bar blends into the canvas (no border/fill) so it
            doesn't read as a second header above the page title — it just floats
            the app-download action top-right. On mobile it's the nav bar. */}
        <div className="flex h-14 items-center gap-3 border-b bg-card px-4 lg:border-0 lg:bg-transparent">
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </button>
          <div className="flex items-center gap-2 lg:hidden">
            <span className="font-bold tracking-tight text-foreground">Clinic Portal</span>
          </div>
          <div className="ml-auto">
            <DownloadAppButton />
          </div>
        </div>

        <main className="flex-1 overflow-auto">
          <PayoutSetupBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
